import { useCustomerAutoshipSubscriptions, useDecisions } from '@libs/common-next';
import { getInventoryStatusLabel, getShippingRestrictionText, validateStockStatus } from '@libs/shared-ui';

import { useProduct } from '@/entities/product';
import { usePurchaseFormWatch } from '@/entities/purchase-form';

export const useSthDeliveryDetails = () => {
    const { product, tailored, isLoading, isError } = useProduct();
    const { autoshipData } = useCustomerAutoshipSubscriptions();
    const { hasAutoship, nextSubscriptionDate } = autoshipData?.data ?? {};
    const autoshipFlag = useDecisions('ff_autoship');
    const isPrecrawl = autoshipFlag === 'precrawl';

    // Minimum qty
    const minQty = product?.deliveryMethods.STH?.purchaseQuantities?.min ?? 1;
    const minimumQuantityText = minQty > 1 ? `Minimum quantity of ${minQty} required` : null;

    // Stock status
    const variant = tailored?.variants[product?.sku ?? ''];
    const availableStock = variant?.STH?.availableStock ?? 0;

    const stockStatus = validateStockStatus({
        stockQuantity: availableStock,
        minimumCaseQty: minQty,
        customPharmacyOOS: Boolean(product?.pharmacy?.oos),
    });

    const poBoxText = getShippingRestrictionText(product?.deliveryMethods?.STH?.poBoxRestricted);

    // Autoship subscriber
    const purchaseOption = usePurchaseFormWatch('purchaseOption') ?? 'one-time';
    const isAutoshipSelected = purchaseOption === 'autoship';

    const nextAutoshipDate = new Date(nextSubscriptionDate ?? '').toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        timeZone: 'UTC',
    });
    const isPharmacy = Boolean(product?.pharmacy);
    const isAutoshipActive = Boolean(isAutoshipSelected && hasAutoship && !isPharmacy);
    const nextAutoshipText = isAutoshipActive ? `Add to your next Autoship on ${nextAutoshipDate}` : null;
    const nextAutoshipSubmittedText = isAutoshipActive
        ? `This item has been added to your next Autoship order on ${nextAutoshipDate}.`
        : null;

    const shouldOmitStockStatus = isError || (isPrecrawl && isAutoshipActive);
    const stockStatusText = shouldOmitStockStatus ? null : getInventoryStatusLabel(stockStatus);

    return {
        poBoxText,
        nextAutoshipText,
        minimumQuantityText,
        isLoading,
        nextAutoshipSubmittedText,
        isInStock: stockStatus === 'InStock',
        outOfStock: stockStatus === 'OutOfStock',
        onlyFewLeft: stockStatus === 'FewLeftInStock',
        stockStatusText,
        isPrecrawl,
    };
};
